import React, { useEffect, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import { useDispatch } from 'react-redux'
import { Dialog, Switch } from '@mui/material'
import { toast } from 'react-toastify'
import Avatar from 'components/Avatar'
import { listEstateUsers } from 'services/estateService'
import * as estateDocumentsService from 'services/estateDocumentsService'
import { hideEmail, isStaffUser } from 'utils/user'
import { toggleEstateFolderAccessRoutine } from 'ducks/estateDocuments/actions'
import { font, ink, line, radius, surface } from 'features/dashboard/tokens'

const CYAN = '#33becc'

const ManageEstateFolderPermissions = ({ estateId, folder, onClose }) => {
  const dispatch = useDispatch()
  const [members, setMembers] = useState([])
  const [granted, setGranted] = useState({})
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let active = true
    setLoading(true)
    Promise.all([
      listEstateUsers(estateId),
      estateDocumentsService.getEstateFolderUsers({ estateId, folderId: folder.id })
    ])
      .then(([usersRes, folderUsersRes]) => {
        if (!active) return
        const rows = (usersRes.data?.data || []).filter(row => row.user && !isStaffUser(row.user))
        setMembers(rows)
        const current = {}
        ;(folderUsersRes.data?.data || []).forEach(userId => { current[userId] = true })
        setGranted(current)
      })
      .catch(() => toast.error('Could not load who has access to this estate'))
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [estateId, folder.id])

  const toggle = (userId, value) => {
    setGranted(g => ({ ...g, [userId]: value }))
    dispatch(toggleEstateFolderAccessRoutine.trigger({
      estateId,
      userId,
      folders: [{ id: folder.id, permission: value }]
    }))
  }

  return (
    <Dialog open onClose={onClose} maxWidth='sm' fullWidth>
      <Container>
        <Title>Manage access: {folder.name}</Title>
        <Hint>
          By default everyone with access to this estate can see this folder. Grant access below to
          restrict it to the chosen people. This cascades to sub-folders.
        </Hint>

        {loading ? (
          <SkeletonRows>
            {Array.from({ length: 4 }).map((_, index) => (
              <SkeletonRow key={index}>
                <SkeletonAvatar />
                <SkeletonLines>
                  <SkeletonLine $w='55%' />
                  <SkeletonLine $w='35%' />
                </SkeletonLines>
              </SkeletonRow>
            ))}
          </SkeletonRows>
        ) : members.length === 0 ? (
          <Empty>No one else has access to this estate yet.</Empty>
        ) : (
          <MemberList>
            {members.map(member => {
              const user = member.user
              const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ')
              return (
                <MemberRow key={user.id}>
                  <Avatar firstName={user.firstName} lastName={user.lastName} email={user.email} size={36} />
                  <MemberInfo>
                    <MemberName>{fullName || hideEmail(user.email)}</MemberName>
                    {fullName && <MemberEmail>{hideEmail(user.email)}</MemberEmail>}
                  </MemberInfo>
                  <Switch
                    checked={!!granted[user.id]}
                    onChange={event => toggle(user.id, event.target.checked)}
                  />
                </MemberRow>
              )
            })}
          </MemberList>
        )}

        <Footer>
          <CancelButton type='button' onClick={onClose}>Done</CancelButton>
        </Footer>
      </Container>
    </Dialog>
  )
}

export default ManageEstateFolderPermissions

const skeletonPulse = keyframes`
  0%, 100% { opacity: 0.55; }
  50%      { opacity: 1; }
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 24px;
  background: ${surface.card};
`

const Title = styled.h2`
  margin: 0;
  font-size: ${font.heading};
  font-weight: 700;
  color: ${ink.base};
`

const Hint = styled.p`
  margin: 0;
  font-size: ${font.body};
  color: ${ink.mute};
  line-height: 1.5;
`

const Empty = styled.p`
  margin: 0;
  padding: 10px 0;
  font-size: ${font.body};
  color: ${ink.mute};
`

const MemberList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: 360px;
  overflow-y: auto;
`

const MemberRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 4px;
  border-bottom: 1px solid ${line.base};

  &:last-child {
    border-bottom: none;
  }
`

const MemberInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
`

const MemberName = styled.span`
  font-size: ${font.body};
  font-weight: 600;
  color: ${ink.base};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const MemberEmail = styled.span`
  font-size: ${font.small};
  color: ${ink.mute};
`

const SkeletonRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 4px 0;
`

const SkeletonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const SkeletonAvatar = styled.span`
  display: block;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #e7e9ee;
  animation: ${skeletonPulse} 1.3s ease-in-out infinite;
`

const SkeletonLines = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const SkeletonLine = styled.span`
  display: block;
  height: 11px;
  width: ${({ $w }) => $w || '100%'};
  border-radius: ${radius.xs};
  background: #e7e9ee;
  animation: ${skeletonPulse} 1.3s ease-in-out infinite;
`

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`

const CancelButton = styled.button`
  min-height: 40px;
  padding: 0 18px;
  border: 1px solid ${line.strong};
  border-radius: ${radius.sm};
  background: ${surface.card};
  color: ${ink.soft};
  font-size: ${font.body};
  font-weight: 600;
  cursor: pointer;
  transition: color 160ms ease;

  &:hover {
    color: ${ink.base};
  }

  &:focus-visible {
    outline: 2px solid ${CYAN};
    outline-offset: 1px;
  }
`
